import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Tooltip,
  Chip,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { Info } from "lucide-react";
import Loading from "../Loading";
import { useVendor } from "../../utils/Context";
import { useErrorToast } from "../../hooks/useErrorToast";
import { useQuery } from "@tanstack/react-query";

const yearOptions = [
  { label: '2024', value: '2024-01-01' },
  { label: '2025', value: '2025-01-01' },
  { label: '2026', value: '2026-01-01' },
];

const fetchCreditAchievements = async ({ queryKey }) => {
  const [, startDate, selectedVendor] = queryKey;

  if (!startDate) return [];

  const token = localStorage.getItem("tokenInv");
  let url = `${process.env.REACT_APP_URL}/api/get_credit_achievement?start_date=${startDate}`;
  if (selectedVendor !== -1) url += `&vendor_id=${selectedVendor}`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data || null;
};

// Number formatting helper (abbreviated, without rounding up)
const formatNumber = (num) => {
  if (!num) return "0.00";
  const numParsed = parseFloat(num);
  if (numParsed >= 1_000_000) {
    const val = Math.trunc((numParsed / 1_000_000) * 100) / 100;
    return val.toFixed(2).replace(/\.?0+$/, "") + "M";
  } else if (numParsed >= 1_000) {
    const val = Math.trunc((numParsed / 1_000) * 100) / 100;
    return val.toFixed(2).replace(/\.?0+$/, "") + "K";
  }
  return numParsed.toFixed(2);
};

// Full number formatter for tooltips
const formatFullNumber = (num) => {
  if (!num) return "0.00";
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function ApproachCreditNote() {
  const showError = useErrorToast();
  const navigate = useNavigate();
  const { selectedVendor } = useVendor();

  // State for selected startDate (controlled by Autocomplete)
  const [startDate, setStartDate] = useState('2026-01-01');

  // Query to fetch credit achievement data based on startDate & vendor
  const {
    data: creditDataRaw = [],
    isLoading: loadingCreditData,
    isError: creditDataError,
    error: creditDataErrorObj,
    refetch: refetchCreditData,
  } = useQuery({
    queryKey: ["creditAchievements", startDate, selectedVendor],
    queryFn: fetchCreditAchievements,
    enabled: !!startDate,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min cache
    onError: (error) => {
      if (error.response?.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        navigate("/");
      } else {
        showError(
          error?.response?.data?.message || "Failed to load credit achievement"
        );
      }
    },
  });

  const chargePeriod = React.useMemo(() => {
    if (creditDataRaw && creditDataRaw.charge_period_range) {
      return `${creditDataRaw.charge_period_range[0]} - ${creditDataRaw.charge_period_range[1]}`;
    }
    return null;
  }, [creditDataRaw]);

  // Transform creditData for table display
  const creditData = React.useMemo(() => {
    if (!creditDataRaw || !creditDataRaw.credit_achievement_rows) return [];

    return creditDataRaw.credit_achievement_rows.map((el, index) => ({
      id: index,
      contract_start_date: dayjs(el.contract_start_date).format("DD-MM-YYYY"),
      from_amount: el.from_amount ? formatNumber(el.from_amount) : "0.00",
      from_amount_full: el.from_amount ? formatFullNumber(el.from_amount) : "0.00",
      to_amount: el.to_amount ? formatNumber(el.to_amount) : "-",
      to_amount_full: el.to_amount ? formatFullNumber(el.to_amount) : "-",
      matching: el.matching ? "Yes" : "No",
      credit_percentage: el.credit_percentage ? `${el.credit_percentage}%` : "-",
      remaining: el.remaining_amount ? formatNumber(el.remaining_amount) : "0.00",
      remaining_full: el.remaining_amount ? formatFullNumber(el.remaining_amount) : "0.00",
      total: el.achived_amount ? formatNumber(el.achived_amount) : "0.00",
      total_full: el.achived_amount ? formatFullNumber(el.achived_amount) : "0.00",
    }));
  }, [creditDataRaw]);

  const isLoading = loadingCreditData;

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
      {isLoading && <Loading />}
      <Toaster position="top-right" reverseOrder={false} />
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: "hidden", flex: 1, display: "flex", flexDirection: "column", height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#1e293b" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                Credit Achievement
                {chargePeriod && (
                  <Chip 
                    label={chargePeriod} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ fontWeight: 600, fontSize: '10px', height: 20 }} 
                  />
                )}
              </Typography>
              <Typography variant="caption" color="#94a3b8" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {creditData.length} record{creditData.length !== 1 ? 's' : ''} found • 
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '350px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Tracks your total invoiced spend for the current calendar year against your eligible credit tiers to show your current achievement level and the amount required to reach the next tier.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        <strong>Verification:</strong> Sum the 'Total USD' from all invoices whose invoice date falls within the current calendar year (January 1st to December 31st), and compare this total against your contract's credit slabs.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span style={{ display: 'flex', alignItems: 'center', cursor: 'help', color: '#64748b' }}>
                    What is this? <Info size={14} style={{ marginLeft: '4px' }} />
                  </span>
                </Tooltip>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                options={yearOptions}
                getOptionLabel={(option) => option.label}
                value={yearOptions.find((opt) => opt.value === startDate) || null}
                onChange={(event, newValue) => {
                  setStartDate(newValue ? newValue.value : '2024-01-01');
                }}
                disableClearable
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    variant="outlined" 
                    placeholder="Select Year"
                    sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', height: '36px', bgcolor: 'white' } }} 
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            <TableContainer sx={{ maxHeight: 250, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {credColumn.map((col) => (
                      <TableCell
                        key={col.field}
                        sx={{
                          fontWeight: 700, fontSize: '11px', backgroundColor: '#f8fafc',
                          color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                          py: 1, whiteSpace: 'nowrap',
                        }}
                      >
                        {col.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditData.length > 0 ? (
                    creditData.map((row, index) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:hover': { backgroundColor: '#f8fafc' },
                          transition: 'background-color 0.15s ease',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {credColumn.map((col) => (
                          <TableCell key={col.field} sx={{ fontSize: '12px', py: 1 }}>
                            {col.renderCell ? col.renderCell({ row, field: col.field, value: row[col.field] }) : row[col.field]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={credColumn.length} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                        No credit achievements found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ApproachCreditNote;

const renderAmountCell = (params) => {
  if (params.value === "-" || !params.value) return "-";
  const fullField = params.field + "_full";
  return (
    <Tooltip title={params.row[fullField]} arrow placement="top">
      <span style={{ cursor: "help", borderBottom: "1px dashed #ccc" }}>
        {params.value}
      </span>
    </Tooltip>
  );
};

const credColumn = [
  // { field: "contract_start_date", headerName: "Start Date", width: 150 },
  { field: "from_amount", headerName: "From Amount", width: 150, renderCell: renderAmountCell },
  { field: "to_amount", headerName: "To Amount", width: 150, renderCell: renderAmountCell },
  { field: "matching", headerName: "Achieved", width: 150 },
  { field: "credit_percentage", headerName: "Percentage", width: 150 },
  { field: "remaining", headerName: "Remaining Amount", width: 150, renderCell: renderAmountCell },
  { field: "total", headerName: "Total Amount", width: 150, renderCell: renderAmountCell },
];
